import React, { useState } from "react";
import styled from "styled-components";
import { Row, Col, Form, Button, Card, Toast, Spinner } from "react-bootstrap";

import API from "../../../network/dashboardAPI";

const Wrapper = styled.div`
  background-color: #f7f7f7;
  position: relative;
  padding: 50px 20px;
  min-height: 100vh;

  @media (max-width: 989px) {
    padding-top: 70px;
  }

  .ds-title {
    font-weight: 700;
    margin-bottom: 4px;
  }
  .ds-sub {
    color: #888;
    margin-bottom: 24px;
  }
  .ds-card {
    margin-bottom: 18px;
  }
  .ds-card .card-title {
    font-size: 15px;
    font-weight: 600;
  }
  .ds-danger .card-title {
    color: #c0392b;
  }
  .ds-response {
    background: #1e1e1e;
    color: #d4d4d4;
    padding: 14px;
    border-radius: 6px;
    font-size: 12px;
    max-height: 520px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const parseJsonField = (value) => {
  if (!value || !value.trim()) return {};
  return JSON.parse(value);
};

const DevSupport = () => {
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);

  // toast
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastBg, setToastBg] = useState("bg-success");

  // per-action inputs
  const [pushMessage, setPushMessage] = useState(true);
  const [pushPdf, setPushPdf] = useState(true);
  const [pushExcel, setPushExcel] = useState(true);
  const [approveStatus, setApproveStatus] = useState("");
  const [outTradeNo, setOutTradeNo] = useState("");
  const [abaTxn, setAbaTxn] = useState("");
  const [orderFields, setOrderFields] = useState("");
  const [paymentFields, setPaymentFields] = useState("");
  const [deleteItemIds, setDeleteItemIds] = useState("");
  const [confirmDeleteItems, setConfirmDeleteItems] = useState(false);
  const [confirmDeletePayment, setConfirmDeletePayment] = useState(false);
  const [deletePaymentId, setDeletePaymentId] = useState("");
  const [walletCustId, setWalletCustId] = useState("");
  const [walletDeleteId, setWalletDeleteId] = useState("");
  const [confirmDeleteWallet, setConfirmDeleteWallet] = useState(false);
  const [notedDeleteId, setNotedDeleteId] = useState("");
  const [confirmDeleteNoted, setConfirmDeleteNoted] = useState(false);
  const [linkDeleteId, setLinkDeleteId] = useState("");
  const [confirmDeleteLink, setConfirmDeleteLink] = useState(false);
  const [walletTxnOwnerId, setWalletTxnOwnerId] = useState("");
  const [walletTxnDeleteId, setWalletTxnDeleteId] = useState("");
  const [confirmDeleteWalletTxn, setConfirmDeleteWalletTxn] = useState(false);

  const notify = (message, isOk = true) => {
    setToastMessage(message);
    setToastBg(isOk ? "bg-success" : "bg-danger");
    setShowToast(true);
  };

  const requireOrderId = () => {
    if (!orderId || !String(orderId).trim()) {
      notify("Please enter an Order ID first.", false);
      return false;
    }
    return true;
  };

  // Runs an API call, unwraps the response and updates UI state.
  const run = async (label, apiCall) => {
    setLoading(true);
    try {
      const res = await apiCall();
      const json = await res.json();
      setResponse({ action: label, ...json });
      if (json && json.success) {
        notify(label + ": success", true);
      } else {
        notify(label + ": " + ((json && json.message) || "failed"), false);
      }
    } catch (e) {
      setResponse({ action: label, error: true, message: e.message });
      notify(label + ": " + e.message, false);
    } finally {
      setLoading(false);
    }
  };

  const onPushTelegram = () => {
    if (!requireOrderId()) return;
    run("Push to Telegram", () =>
      API.devSupportPushTelegram(orderId, { message: pushMessage, pdf: pushPdf, excel: pushExcel })
    );
  };

  const onApprove = () => {
    if (!requireOrderId()) return;
    const body = {};
    if (approveStatus !== "") body.status = Number(approveStatus);
    run("Approve order", () => API.devSupportApproveOrder(orderId, body));
  };

  const onQueryKess = () => {
    if (!outTradeNo || !outTradeNo.trim()) {
      notify("Enter an out_trade_no to query KESS.", false);
      return;
    }
    run("Query KESS", () => API.devSupportQueryKess({ out_trade_no: outTradeNo.trim() }));
  };

  const onQueryAba = () => {
    if (!requireOrderId()) return;
    const body = {};
    if (abaTxn && abaTxn.trim()) body.p_txnld = abaTxn.trim();
    run("Query ABA", () => API.devSupportQueryAba(orderId, body));
  };

  const onMarkPaid = () => {
    if (!requireOrderId()) return;
    run("Mark as paid", () => API.devSupportMarkPaid(orderId));
  };

  const onUpdateOrder = () => {
    if (!requireOrderId()) return;
    let body;
    try {
      body = parseJsonField(orderFields);
    } catch (e) {
      notify("Order fields must be valid JSON.", false);
      return;
    }
    run("Update order", () => API.devSupportUpdateOrder(orderId, body));
  };

  const onUpdatePayment = () => {
    if (!requireOrderId()) return;
    let body;
    try {
      body = parseJsonField(paymentFields);
    } catch (e) {
      notify("Payment fields must be valid JSON.", false);
      return;
    }
    run("Update order payment", () => API.devSupportUpdateOrderPayment(orderId, body));
  };

  const onDeleteItems = () => {
    if (!confirmDeleteItems) {
      notify("Tick the confirm box to hard-delete items.", false);
      return;
    }
    const ids = deleteItemIds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number)
      .filter((n) => !Number.isNaN(n));

    const body = { confirm: true };
    if (ids.length) body.orderItemIds = ids;
    else if (orderId) body.order_id = Number(orderId);
    else {
      notify("Provide order item IDs or an Order ID.", false);
      return;
    }
    if (!window.confirm("Permanently delete these order item(s)? This cannot be undone.")) return;
    run("Hard delete order items", () => API.devSupportHardDeleteOrderItems(body));
  };

  const onListPayments = () => {
    if (!requireOrderId()) return;
    run("List order payments", () => API.devSupportGetOrderPayments(orderId));
  };

  const onDeletePayment = () => {
    if (!requireOrderId()) return;
    if (!confirmDeletePayment) {
      notify("Tick the confirm box to hard-delete the payment.", false);
      return;
    }
    const body = { confirm: true };
    if (deletePaymentId && String(deletePaymentId).trim()) {
      body.payment_id = Number(deletePaymentId);
    }
    const target = body.payment_id
      ? "payment #" + body.payment_id
      : "ALL payment record(s) for order #" + orderId;
    if (!window.confirm("Permanently delete " + target + "? This cannot be undone.")) return;
    run("Hard delete order payment", () => API.devSupportHardDeleteOrderPayment(orderId, body));
  };

  const onListWallets = () => {
    if (!walletCustId || !String(walletCustId).trim()) { notify("Enter a customer ID.", false); return; }
    run("List wallets", () => API.devSupportGetWallets(walletCustId.trim()));
  };

  const onDeleteWallet = () => {
    if (!walletDeleteId || !String(walletDeleteId).trim()) { notify("Enter a wallet id.", false); return; }
    if (!confirmDeleteWallet) { notify("Tick the confirm box to delete the wallet.", false); return; }
    if (!window.confirm("Permanently delete wallet #" + walletDeleteId + "? This cannot be undone.")) return;
    run("Delete wallet", () => API.devSupportDeleteWallet(walletDeleteId, { confirm: true }));
  };

  const onListPaymentNoteds = () => {
    if (!requireOrderId()) return;
    run("List payment noteds", () => API.devSupportGetPaymentNoteds(orderId));
  };

  const onDeletePaymentNoted = () => {
    if (!notedDeleteId || !String(notedDeleteId).trim()) { notify("Enter a payment-noted id.", false); return; }
    if (!confirmDeleteNoted) { notify("Tick the confirm box to delete the payment noted.", false); return; }
    if (!window.confirm("Permanently delete payment-noted #" + notedDeleteId + "? This cannot be undone.")) return;
    run("Delete payment noted", () => API.devSupportDeletePaymentNoted(notedDeleteId, { confirm: true }));
  };

  const onListPaymentLinks = () => {
    if (!requireOrderId()) return;
    run("List payment links", () => API.devSupportGetPaymentLinks(orderId));
  };

  const onDeletePaymentLink = () => {
    if (!linkDeleteId || !String(linkDeleteId).trim()) { notify("Enter a payment link id.", false); return; }
    if (!confirmDeleteLink) { notify("Tick the confirm box to delete the payment link.", false); return; }
    if (!window.confirm("Permanently delete payment link #" + linkDeleteId + "? This cannot be undone.")) return;
    run("Delete payment link", () => API.devSupportDeletePaymentLink(linkDeleteId, { confirm: true }));
  };

  const onListWalletTxns = () => {
    if (!walletTxnOwnerId || !String(walletTxnOwnerId).trim()) { notify("Enter a wallet owner (customer) ID.", false); return; }
    run("List wallet transactions", () => API.devSupportGetWalletTransactions(walletTxnOwnerId.trim()));
  };

  const onDeleteWalletTxn = () => {
    if (!walletTxnDeleteId || !String(walletTxnDeleteId).trim()) { notify("Enter a wallet transaction id.", false); return; }
    if (!confirmDeleteWalletTxn) { notify("Tick the confirm box to delete the wallet transaction.", false); return; }
    if (!window.confirm("Permanently delete wallet transaction #" + walletTxnDeleteId + "? This cannot be undone.")) return;
    run("Delete wallet transaction", () => API.devSupportDeleteWalletTransaction(walletTxnDeleteId, { confirm: true }));
  };

  return (
    <Wrapper>
      <Toast
        className={toastBg}
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={3500}
        autohide
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1080, color: "#fff" }}
      >
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>

      <h3 className="ds-title">Dev Support</h3>
      <p className="ds-sub">
        Developer / customer-support tools. Actions here are low-level and some are destructive &mdash; use with care.
      </p>

      <Card className="ds-card">
        <Card.Body>
          <Form.Group className="mb-0">
            <Form.Label className="card-title">Order ID</Form.Label>
            <Form.Control
              type="number"
              placeholder="e.g. 96776"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              style={{ maxWidth: 260 }}
            />
            <Form.Text muted>Used by all order-scoped actions below.</Form.Text>
          </Form.Group>
        </Card.Body>
      </Card>

      <Row>
        <Col lg={7}>
          {/* Push to Telegram */}
          <Card className="ds-card">
            <Card.Body>
              <div className="card-title mb-2">Push to Telegram</div>
              <Form.Check inline label="Message" checked={pushMessage} onChange={(e) => setPushMessage(e.target.checked)} />
              <Form.Check inline label="Invoice PDF" checked={pushPdf} onChange={(e) => setPushPdf(e.target.checked)} />
              <Form.Check inline label="Excel" checked={pushExcel} onChange={(e) => setPushExcel(e.target.checked)} />
              <div className="mt-2">
                <Button size="sm" onClick={onPushTelegram} disabled={loading}>Push order</Button>
              </div>
            </Card.Body>
          </Card>

          {/* Approve */}
          <Card className="ds-card">
            <Card.Body>
              <div className="card-title mb-2">Approve order</div>
              <Form.Control
                type="number"
                placeholder="Target status (blank = PROCESSING / 3)"
                value={approveStatus}
                onChange={(e) => setApproveStatus(e.target.value)}
                style={{ maxWidth: 320 }}
              />
              <div className="mt-2">
                <Button size="sm" onClick={onApprove} disabled={loading}>Approve</Button>
              </div>
            </Card.Body>
          </Card>

          {/* Query KESS */}
          <Card className="ds-card">
            <Card.Body>
              <div className="card-title mb-2">Query KESS</div>
              <Form.Control
                placeholder="out_trade_no"
                value={outTradeNo}
                onChange={(e) => setOutTradeNo(e.target.value)}
                style={{ maxWidth: 360 }}
              />
              <div className="mt-2">
                <Button size="sm" onClick={onQueryKess} disabled={loading}>Query KESS</Button>
              </div>
            </Card.Body>
          </Card>

          {/* Query ABA */}
          <Card className="ds-card">
            <Card.Body>
              <div className="card-title mb-2">Query ABA</div>
              <Form.Control
                placeholder="p_txnld (blank = use order's stored value)"
                value={abaTxn}
                onChange={(e) => setAbaTxn(e.target.value)}
                style={{ maxWidth: 360 }}
              />
              <div className="mt-2">
                <Button size="sm" onClick={onQueryAba} disabled={loading}>Query ABA</Button>
              </div>
            </Card.Body>
          </Card>

          {/* Mark paid */}
          <Card className="ds-card">
            <Card.Body>
              <div className="card-title mb-2">Mark as paid</div>
              <Button size="sm" variant="success" onClick={onMarkPaid} disabled={loading}>Mark order paid</Button>
            </Card.Body>
          </Card>

          {/* Update order */}
          <Card className="ds-card">
            <Card.Body>
              <div className="card-title mb-2">Update order info</div>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder='JSON, e.g. {"delivery_fee": 1.5, "comments": "..."}'
                value={orderFields}
                onChange={(e) => setOrderFields(e.target.value)}
              />
              <Form.Text muted>
                Allowed: status, order_total, item_total, delivery_fee, coupon_discount_amount, driver_tip, wallet_payment,
                comments, oos_option, payment_method_name, self_deliver, slot_id, slot_type, slot_range. Slot example:{" "}
                {'{"slot_type":"medium","slot_range":[1201,1202]}'} or {'{"slot_type":"hourly","slot_id":1203}'}.
              </Form.Text>
              <div className="mt-2">
                <Button size="sm" onClick={onUpdateOrder} disabled={loading}>Update order</Button>
              </div>
            </Card.Body>
          </Card>

          {/* Update payment */}
          <Card className="ds-card">
            <Card.Body>
              <div className="card-title mb-2">Update order payment</div>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder='JSON, e.g. {"amount": 12.5, "is_paid": 1}'
                value={paymentFields}
                onChange={(e) => setPaymentFields(e.target.value)}
              />
              <Form.Text muted>Allowed: amount, is_paid, payment_reference, payment_method_name, p_txnld, payment_option, payment_status.</Form.Text>
              <div className="mt-2">
                <Button size="sm" onClick={onUpdatePayment} disabled={loading}>Update payment</Button>
              </div>
            </Card.Body>
          </Card>

          {/* Hard delete items */}
          <Card className="ds-card ds-danger">
            <Card.Body>
              <div className="card-title mb-2">Hard delete order items</div>
              <Form.Control
                placeholder="Order item IDs, comma-separated (blank = all items of Order ID)"
                value={deleteItemIds}
                onChange={(e) => setDeleteItemIds(e.target.value)}
              />
              <Form.Check
                className="mt-2"
                label="I understand this permanently deletes rows"
                checked={confirmDeleteItems}
                onChange={(e) => setConfirmDeleteItems(e.target.checked)}
              />
              <div className="mt-2">
                <Button size="sm" variant="danger" onClick={onDeleteItems} disabled={loading}>Delete items</Button>
              </div>
            </Card.Body>
          </Card>

          {/* Hard delete payment */}
          <Card className="ds-card ds-danger">
            <Card.Body>
              <div className="card-title mb-2">Hard delete order payment</div>
              <Button size="sm" variant="outline-secondary" className="mb-2" onClick={onListPayments} disabled={loading}>
                List payments
              </Button>
              <Form.Control
                type="number"
                placeholder="payment_id (blank = ALL payments for this order)"
                value={deletePaymentId}
                onChange={(e) => setDeletePaymentId(e.target.value)}
                style={{ maxWidth: 400 }}
              />
              <Form.Text muted>Use "List payments" to find the duplicate's payment_id, then delete just that one.</Form.Text>
              <Form.Check
                className="mt-2"
                label="I understand this permanently deletes the payment record"
                checked={confirmDeletePayment}
                onChange={(e) => setConfirmDeletePayment(e.target.checked)}
              />
              <div className="mt-2">
                <Button size="sm" variant="danger" onClick={onDeletePayment} disabled={loading}>Delete payment</Button>
              </div>
            </Card.Body>
          </Card>

          {/* Delete wallet */}
          <Card className="ds-card ds-danger">
            <Card.Body>
              <div className="card-title mb-2">Delete wallet (by id)</div>
              <Form.Control className="mb-2" placeholder="Customer ID (to list wallets)" value={walletCustId} onChange={(e) => setWalletCustId(e.target.value)} style={{ maxWidth: 400 }} />
              <Button size="sm" variant="outline-secondary" className="mb-2" onClick={onListWallets} disabled={loading}>List wallets</Button>
              <Form.Control type="number" placeholder="wallet id" value={walletDeleteId} onChange={(e) => setWalletDeleteId(e.target.value)} style={{ maxWidth: 400 }} />
              <Form.Check className="mt-2" label="I understand this permanently deletes the wallet row" checked={confirmDeleteWallet} onChange={(e) => setConfirmDeleteWallet(e.target.checked)} />
              <div className="mt-2"><Button size="sm" variant="danger" onClick={onDeleteWallet} disabled={loading}>Delete wallet</Button></div>
            </Card.Body>
          </Card>

          {/* Delete wallet transaction */}
          <Card className="ds-card ds-danger">
            <Card.Body>
              <div className="card-title mb-2">Delete wallet transaction (by id)</div>
              <Form.Control className="mb-2" placeholder="Wallet owner (customer) ID (to list transactions)" value={walletTxnOwnerId} onChange={(e) => setWalletTxnOwnerId(e.target.value)} style={{ maxWidth: 400 }} />
              <Button size="sm" variant="outline-secondary" className="mb-2" onClick={onListWalletTxns} disabled={loading}>List wallet transactions</Button>
              <Form.Control type="number" placeholder="wallet transaction id" value={walletTxnDeleteId} onChange={(e) => setWalletTxnDeleteId(e.target.value)} style={{ maxWidth: 400 }} />
              <Form.Check className="mt-2" label="I understand this permanently deletes the wallet transaction" checked={confirmDeleteWalletTxn} onChange={(e) => setConfirmDeleteWalletTxn(e.target.checked)} />
              <div className="mt-2"><Button size="sm" variant="danger" onClick={onDeleteWalletTxn} disabled={loading}>Delete wallet transaction</Button></div>
            </Card.Body>
          </Card>

          {/* Delete payment noted */}
          <Card className="ds-card ds-danger">
            <Card.Body>
              <div className="card-title mb-2">Delete payment noted (by id)</div>
              <Button size="sm" variant="outline-secondary" className="mb-2" onClick={onListPaymentNoteds} disabled={loading}>List payment noteds (for Order ID)</Button>
              <Form.Control type="number" placeholder="payment_noted id" value={notedDeleteId} onChange={(e) => setNotedDeleteId(e.target.value)} style={{ maxWidth: 400 }} />
              <Form.Check className="mt-2" label="I understand this permanently deletes the payment-noted row" checked={confirmDeleteNoted} onChange={(e) => setConfirmDeleteNoted(e.target.checked)} />
              <div className="mt-2"><Button size="sm" variant="danger" onClick={onDeletePaymentNoted} disabled={loading}>Delete payment noted</Button></div>
            </Card.Body>
          </Card>

          {/* Delete generated payment link */}
          <Card className="ds-card ds-danger">
            <Card.Body>
              <div className="card-title mb-2">Delete generated payment link (by id)</div>
              <Button size="sm" variant="outline-secondary" className="mb-2" onClick={onListPaymentLinks} disabled={loading}>List payment links (for Order ID)</Button>
              <Form.Control type="number" placeholder="payment link id" value={linkDeleteId} onChange={(e) => setLinkDeleteId(e.target.value)} style={{ maxWidth: 400 }} />
              <Form.Check className="mt-2" label="I understand this permanently deletes the payment link" checked={confirmDeleteLink} onChange={(e) => setConfirmDeleteLink(e.target.checked)} />
              <div className="mt-2"><Button size="sm" variant="danger" onClick={onDeletePaymentLink} disabled={loading}>Delete payment link</Button></div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="ds-card" style={{ position: "sticky", top: 20 }}>
            <Card.Body>
              <div className="card-title mb-2">
                Response {loading && <Spinner as="span" animation="border" size="sm" className="ml-2" />}
              </div>
              <pre className="ds-response">
                {response ? JSON.stringify(response, null, 2) : "Run an action to see the response here."}
              </pre>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Wrapper>
  );
};

export default DevSupport;
